/**
 * The `statistics_scope` marker a served table structure carries, and the rules for showing it.
 *
 * A table structure used to be one thing: the numbers described the whole table, so a figure could
 * be printed on its own and meant what it looked like. Since the backend started serving
 * department-scoped structures that is no longer true. The same table returns different numbers to
 * two members of two departments, and one of those numbers can be a lower bound rather than a
 * measurement. The marker is what tells them apart, so a figure rendered without consulting it is
 * a figure the reader cannot interpret.
 *
 * Everything here is pure and framework-free on purpose: the upload dialog, the metadata viewer and
 * the agent trace all render structures, and only one of the three renders them through Angular
 * bindings. Copy is returned as a translation key plus params rather than as text so each surface
 * translates in its own way and no English leaks into a template.
 */

import { escapeHtml } from './escape-html';

/**
 * Where the numbers came from.
 *
 * Two backend types write this slot and they do not share a vocabulary. `ServedStructureSource`
 * (`served-structure.ts`) supplies `legacy` / `scope` / `set-scan` / `set-atoms` / `base-sanitized`
 * / `denied`, and the set assembler stamps `SetStatsSource` (`scope-stats.types.ts`) straight
 * through, which is `scan` / `atoms`. Both spellings reach this app, so both are named here.
 * Anything else is `unknown`: a new server value must not make a caption disappear.
 */
export type StatisticsScopeSource =
    | 'legacy'
    | 'scope'
    | 'set-scan'
    | 'set-atoms'
    | 'scan'
    | 'atoms'
    | 'base-sanitized'
    | 'denied'
    | 'unknown';

/** Parsed form of the root `statistics_scope` object. */
export interface TableStatisticsScope {
    /** Ascending unique department ids the numbers describe. Empty for a whole-table read. */
    departmentIds: number[];
    source: StatisticsScopeSource;
    /**
     * True only when every readable column carries an exact distinct count. Null means UNKNOWN,
     * which is what a structure served while a build is still running carries, so null must never
     * be collapsed into false.
     */
    exact: boolean | null;
    /** ISO instant the statistics were computed, where the server knew it. */
    generatedAt: string | null;
    /**
     * Column name to the UPPER bound of its distinct count. A column named here has a RANGE and
     * not a measurement: the `distinct_count` slot holds the lower bound. Empty when every served
     * distinct count is exact.
     */
    bounded: Readonly<Record<string, number>>;
}

/** A user-facing string as a Transloco key plus its interpolation params. */
export interface StatisticsScopeText {
    key: string;
    params?: Record<string, string | number>;
}

const KNOWN_SOURCES: ReadonlySet<string> = new Set<string>([
    'legacy',
    'scope',
    'set-scan',
    'set-atoms',
    'scan',
    'atoms',
    'base-sanitized',
    'denied',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function readDepartmentIds(value: unknown): number[] {
    if (!Array.isArray(value)) {
        return [];
    }
    const ids = value
        .map(entry => Number(entry))
        .filter(id => Number.isFinite(id));
    return [...new Set(ids)].sort((a, b) => a - b);
}

function readBounded(value: unknown): Record<string, number> {
    if (!isRecord(value)) {
        return {};
    }
    const bounded: Record<string, number> = {};
    for (const [column, upper] of Object.entries(value)) {
        const parsed = Number(upper);
        if (column && Number.isFinite(parsed)) {
            bounded[column] = parsed;
        }
    }
    return bounded;
}

/**
 * Read the marker off a parsed structure root, or null when the structure carries none.
 *
 * No marker is not the same as an empty one: it means the structure was never scoped, so its
 * numbers describe the whole table and nothing should be captioned.
 */
export function readTableStatisticsScope(root: unknown): TableStatisticsScope | null {
    if (!isRecord(root)) {
        return null;
    }
    const raw = root['statistics_scope'];
    if (!isRecord(raw)) {
        return null;
    }
    const source = String(raw['source'] ?? '').trim();
    const exact = raw['exact'];
    const generatedAt = raw['generated_at'];

    return {
        departmentIds: readDepartmentIds(raw['department_ids']),
        source: (KNOWN_SOURCES.has(source) ? source : 'unknown') as StatisticsScopeSource,
        exact: typeof exact === 'boolean' ? exact : null,
        generatedAt:
            typeof generatedAt === 'string' && generatedAt.trim()
                ? generatedAt.trim()
                : null,
        bounded: readBounded(raw['bounded']),
    };
}

/**
 * True when the numbers were stripped and the set's own statistics have not been computed yet.
 *
 * `base-sanitized` is the server saying it had nothing for this set and queued a build; an unknown
 * `exact` says the same thing from the other side. Either way the reader is looking at a
 * departmental table whose figures are not there yet, which is worth saying out loud rather than
 * showing as a blank cell.
 */
export function statisticsScopeIsPending(
    scope: TableStatisticsScope | null | undefined
): boolean {
    if (!scope || !scope.departmentIds.length) {
        return false;
    }
    return scope.source === 'base-sanitized' || scope.exact === null;
}

/** The upper bound of a column's distinct count, or null when the count is a measurement. */
export function distinctCountUpperBound(
    scope: TableStatisticsScope | null | undefined,
    columnName: string | null | undefined
): number | null {
    if (!scope || !columnName) {
        return null;
    }
    const upper = scope.bounded[columnName];
    return typeof upper === 'number' && Number.isFinite(upper) ? upper : null;
}

/** True when this column's distinct count is a range rather than a measurement. */
export function isDistinctCountBounded(
    scope: TableStatisticsScope | null | undefined,
    columnName: string | null | undefined
): boolean {
    return distinctCountUpperBound(scope, columnName) !== null;
}

/**
 * The distinct-count figure a `distinct_count` slot renders, given the marker.
 *
 * A bounded column renders as a range because the slot holds only the lower bound. Printing it as
 * a plain count would state a number the server never measured.
 */
export function describeDistinctCount(
    rawValue: unknown,
    columnName: string | null | undefined,
    scope: TableStatisticsScope | null | undefined
): StatisticsScopeText {
    const lower = readDistinctCount(rawValue);
    const upper = distinctCountUpperBound(scope, columnName);
    if (upper !== null && upper > lower) {
        return {
            key: '{{lower}} to {{upper}} distinct values (approximate)',
            params: { lower, upper },
        };
    }
    if (upper !== null) {
        return {
            key: 'At least {{count}} distinct values (approximate)',
            params: { count: lower },
        };
    }
    return { key: '{{count}} Distinct Values', params: { count: lower } };
}

/**
 * The count a `distinct_count` slot holds. The slot has carried three shapes over the years: a
 * bare number, the rendered "N Distinct Values" text, and a comma-joined list of the values
 * themselves, which is counted rather than parsed.
 */
export function readDistinctCount(rawValue: unknown): number {
    if (rawValue == null) {
        return 0;
    }
    const normalized = String(rawValue).trim();
    if (!normalized) {
        return 0;
    }
    const explicitCountMatch = normalized.match(/^(\d+)(?:\s*distinct values?)?$/i);
    if (explicitCountMatch) {
        return Number(explicitCountMatch[1]);
    }
    return normalized
        .split(',')
        .map(value => value.trim())
        .filter(Boolean).length;
}

/** The listed values behind a distinct count, or empty when the slot held a count. */
export function readDistinctValuesTooltip(rawValue: unknown): string {
    if (rawValue == null) {
        return '';
    }
    const normalized = String(rawValue).trim();
    return /^(\d+)(?:\s*distinct values?)?$/i.test(normalized) ? '' : normalized;
}

/** Whether an `is_unique` slot means unique, across the spellings the engines write. */
export function readIsUniqueFlag(value: unknown): boolean {
    const normalized = String(value ?? '')
        .trim()
        .toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
}

/**
 * Every line to show beside a structure's figures: which departments they describe, whether a
 * build is still running, and whether any distinct count is a range.
 *
 * Returns an empty list for a structure with no marker, which is a whole-table read that needs no
 * caption at all.
 */
export function statisticsScopeNotes(
    scope: TableStatisticsScope | null | undefined
): StatisticsScopeText[] {
    if (!scope || !scope.departmentIds.length) {
        return [];
    }
    const notes: StatisticsScopeText[] = [];
    const departments = scope.departmentIds.join(', ');
    notes.push(
        scope.departmentIds.length === 1
            ? { key: 'These figures describe department {{departments}} only, not the whole table.', params: { departments } }
            : { key: 'These figures describe departments {{departments}} only, not the whole table.', params: { departments } }
    );

    if (statisticsScopeIsPending(scope)) {
        notes.push({
            key: 'Statistics for these departments are still being prepared, so figures are missing rather than zero.',
        });
        return notes;
    }

    const boundedColumns = Object.keys(scope.bounded);
    if (boundedColumns.length) {
        notes.push({
            key: 'Distinct counts shown as a range are derived rather than counted: {{columns}}.',
            params: { columns: boundedColumns.join(', ') },
        });
    }
    return notes;
}

/**
 * The same notes as an HTML fragment, for the one surface that builds markup as a string instead
 * of binding it. Kept here so the trace and the two dialogs cannot drift apart in what they say.
 */
export function renderStatisticsScopeNotesHtml(
    scope: TableStatisticsScope | null | undefined,
    translate: (key: string, params?: Record<string, string | number>) => string
): string {
    const notes = statisticsScopeNotes(scope);
    if (!notes.length) {
        return '';
    }
    return notes
        .map(
            note =>
                '<p class="text-secondary text-sm mb-1">' +
                escapeHtml(translate(note.key, note.params)) +
                '</p>'
        )
        .join('');
}
